import type { TFunction } from "i18next";
import { formatUpdatedAt } from "@/features/prompts/lib/prompt-list";
import { POST_FLIGHT_OPPORTUNITIES_STALE_TIME_MS } from "@/lib/query-constants";

const JUST_NOW_MS = 60_000;

/**
 * How long this tab may keep treating the list as fresh. Counted from the API's
 * `fetchedAt`, not from when the tab loaded it: the API cache is shared by the
 * tenant, so a tab can receive a list someone else fetched hours earlier, and
 * double-down rejects it once the API's copy is stale. A missing or unreadable
 * `fetchedAt`, or one ahead of the browser clock, gets the full window.
 */
export function postFlightListFreshForMs(fetchedAt: string | null | undefined, now: number): number {
  const fetchedAtMs = parseFetchedAt(fetchedAt);
  if (fetchedAtMs === null) return POST_FLIGHT_OPPORTUNITIES_STALE_TIME_MS;
  const remainingMs = POST_FLIGHT_OPPORTUNITIES_STALE_TIME_MS - (now - fetchedAtMs);
  return Math.min(POST_FLIGHT_OPPORTUNITIES_STALE_TIME_MS, Math.max(0, remainingMs));
}

/**
 * "Updated just now" under a minute, otherwise "Updated 3 hours ago". The shared
 * formatter rounds anything under a minute up to "1 minute ago". A `fetchedAt`
 * ahead of the browser clock also reads as just now.
 */
export function postFlightUpdatedLabel(t: TFunction, fetchedAt: string, locale: string, now: number): string {
  if (isJustFetched(parseFetchedAt(fetchedAt), now)) {
    return t("postFlight.refresh.updatedJustNow", "Updated just now");
  }
  return t("postFlight.refresh.updated", "Updated {{time}}", {
    time: formatUpdatedAt(fetchedAt, locale, now),
  });
}

/**
 * Toast copy for a failed refresh. Names the age of the list the page keeps showing, using the
 * same "just now" rule as the label so the two never disagree.
 */
export function postFlightRefreshFailureMessage(
  t: TFunction,
  fetchedAt: string | null | undefined,
  locale: string,
  now: number,
): string {
  const fetchedAtMs = parseFetchedAt(fetchedAt);
  if (!fetchedAt || fetchedAtMs === null) {
    return t("postFlight.refresh.errorNoList", "Couldn't refresh the list. Try again.");
  }
  if (isJustFetched(fetchedAtMs, now)) {
    return t("postFlight.refresh.errorJustNow", "Couldn't refresh the list. The one on screen was updated just now.");
  }
  return t("postFlight.refresh.error", "Couldn't refresh the list. Showing the one from {{time}}.", {
    time: formatUpdatedAt(fetchedAt, locale, now),
  });
}

function isJustFetched(fetchedAtMs: number | null, now: number): boolean {
  return fetchedAtMs !== null && now - fetchedAtMs < JUST_NOW_MS;
}

function parseFetchedAt(fetchedAt: string | null | undefined): number | null {
  if (!fetchedAt) return null;
  const parsed = Date.parse(fetchedAt);
  return Number.isFinite(parsed) ? parsed : null;
}
